import { Fragment, type ReactNode } from 'react';
import clsx from 'clsx';
import {
  getErrorContainerClassName,
  getTextAlignClass,
  getTextTransformClass,
  type TextFieldSlotPosition,
  type TextFieldTitlePosition,
} from './helpers.ts';

export type TextFieldLayoutSlot = {
  position: TextFieldSlotPosition;
  content: ReactNode;
};

type FieldLabelProps = {
  inputId: string;
  label: string;
  required: boolean;
  showAsterisk: boolean;
  titlePosition: TextFieldTitlePosition;
  alignment: unknown;
  textTransform: unknown;
};

export function FieldLabel({
  inputId,
  label,
  required,
  showAsterisk,
  titlePosition,
  alignment,
  textTransform,
}: FieldLabelProps): JSX.Element {
  return (
    <label
      htmlFor={inputId}
      className={clsx(
        'text-field-title',
        getTextAlignClass(alignment),
        getTextTransformClass(textTransform),
        titlePosition === 'beside' && 'text-field-title-beside'
      )}
    >
      {label}
      {required && showAsterisk ? <span aria-hidden="true"> *</span> : null}
    </label>
  );
}

type FieldRequiredLabelProps = {
  label: string;
  alignment: unknown;
  textTransform: unknown;
};

export function FieldRequiredLabel({
  label,
  alignment,
  textTransform,
}: FieldRequiredLabelProps): JSX.Element {
  return (
    <p
      className={clsx(
        'text-field-required-label',
        getTextAlignClass(alignment),
        getTextTransformClass(textTransform)
      )}
    >
      {label}
    </p>
  );
}

type FieldCharacterCountProps = {
  id: string;
  text: string;
  hasError: boolean;
  alignment: unknown;
};

export function FieldCharacterCount({
  id,
  text,
  hasError,
  alignment,
}: FieldCharacterCountProps): JSX.Element {
  return (
    <p
      id={id}
      className={clsx(
        'text-field-character-count',
        hasError && 'text-field-character-count-error',
        getTextAlignClass(alignment)
      )}
    >
      {text}
    </p>
  );
}

type FieldDescriptionProps = {
  id: string;
  description: string;
  alignment: unknown;
  textTransform: unknown;
};

export function FieldDescription({
  id,
  description,
  alignment,
  textTransform,
}: FieldDescriptionProps): JSX.Element {
  return (
    <p
      id={id}
      className={clsx(
        'text-field-description',
        getTextAlignClass(alignment),
        getTextTransformClass(textTransform)
      )}
    >
      {description}
    </p>
  );
}

type FieldErrorProps = {
  id: string;
  message: string;
  alignment: unknown;
  textTransform: unknown;
  display: unknown;
  assetVerticalAlignment: unknown;
  assetUrl?: string;
};

export function FieldError({
  id,
  message,
  alignment,
  textTransform,
  display,
  assetVerticalAlignment,
  assetUrl,
}: FieldErrorProps): JSX.Element {
  return (
    <div
      id={id}
      role="alert"
      className={getErrorContainerClassName({
        display,
        verticalAlignment: assetVerticalAlignment,
        hasAsset: Boolean(assetUrl),
      })}
    >
      {assetUrl ? (
        <img className="text-field-error-asset" src={assetUrl} alt="" />
      ) : null}
      <p
        className={clsx(
          'text-field-error min-w-0 flex-1',
          getTextAlignClass(alignment),
          getTextTransformClass(textTransform)
        )}
      >
        {message}
      </p>
    </div>
  );
}

type TextFieldLayoutProps = {
  wrapperClassName: string;
  titlePosition: TextFieldTitlePosition;
  titleBesideAlignment: string;
  title: ReactNode;
  description?: ReactNode;
  input: ReactNode;
  error?: ReactNode;
  slots: TextFieldLayoutSlot[];
};

function getSlotsAt(input: {
  slots: TextFieldLayoutSlot[];
  position: TextFieldSlotPosition;
}): ReactNode[] {
  return input.slots
    .filter((slot) => slot.position === input.position)
    .map((slot, index) => (
      <Fragment key={`${input.position}-${index}`}>{slot.content}</Fragment>
    ));
}

export function TextFieldLayout({
  wrapperClassName,
  titlePosition,
  titleBesideAlignment,
  title,
  description,
  input,
  error,
  slots,
}: TextFieldLayoutProps): JSX.Element {
  const above = getSlotsAt({ slots, position: 'above_input' });
  const below = getSlotsAt({ slots, position: 'below_input' });

  const fieldColumn = (
    <>
      {titlePosition === 'above' ? description : null}
      {above}
      {input}
      {below}
    </>
  );

  return (
    <div className={wrapperClassName}>
      {titlePosition === 'beside' ? (
        <div className={clsx('text-field-title-row', titleBesideAlignment)}>
          {title}
          <div className="min-w-0 flex-1">{fieldColumn}</div>
        </div>
      ) : (
        <div className="text-field-title-stack">
          {title}
          {fieldColumn}
        </div>
      )}
      {error}
    </div>
  );
}
